import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { Box, LinearProgress, MenuItem, Select, TextField } from '@mui/material'
import { ui, tone } from '../../../utils/uiTokens'
import { CRM_POLL, CrmCredentials, CrmManager, crmApi } from '../../../features/crm/crmApi'
import {
    Card, CardHead, Chip, Chips, CrmDialog, ErrorText, FieldLabel, Hint, Kpis, Muted, OutlineButton, PageHead, PeriodBar, Pill, PrimaryButton, TONES,
    TableWrap, contentSx, pageSx, usePeriod,
} from '../shared/crmUi'
import { FIRE_REASONS, SALES_GROUPS, dayKeyOf, dictLabel, digits, fmtDate, formatPhone, initials, money, todayKey } from '../shared/format'
import { useCrmDict } from '../shared/dict'
import { sellerStage } from '../shared/stages'
import { EmptyRow } from '../head/HeadCrmNewPage'
import { avatar } from '../moderator/IntakesPage'

type SortKey = 'name' | 'count' | 'turnover' | 'avgCheck' | 'returns'

const RopManagersPage = () => {
    const { formatMessage } = useIntl()
    const period = usePeriod('month')
    const range = { from: period.from, to: period.to }
    const { data: managers = [], isFetching } = crmApi.useGetManagersQuery(range, { pollingInterval: CRM_POLL })
    const { data: report } = crmApi.useGetSalesReportQuery(range)
    const [group, setGroup] = useState('')
    const [fired, setFired] = useState(false)
    const [sort, setSort] = useState<SortKey>('turnover')
    const [dir, setDir] = useState(-1)
    const [open, setOpen] = useState<Record<number, boolean>>({})
    const [adding, setAdding] = useState(false)
    const [added, setAdded] = useState('')
    const [cred, setCred] = useState<CrmManager | null>(null)
    const [manage, setManage] = useState<CrmManager | null>(null)

    const firedN = managers.filter((m) => m.firedAt).length
    const live = managers.filter((m) => !!m.firedAt === fired)
    const val = (m: CrmManager, k: SortKey) => (k === 'name' ? 0 : m.stat?.[k] || 0)
    const list = live
        .filter((m) => !group || m.salesGroup === group)
        .sort((a, b) => {
            const ta = a.stat?.turnover || 0
            const tb = b.stat?.turnover || 0
            // без оборота - всегда вниз
            if (!ta !== !tb) return ta ? -1 : 1
            return (sort === 'name' ? (a.name || '').localeCompare(b.name || '') : val(a, sort) - val(b, sort)) * dir
        })
    const sum = (k: 'count' | 'turnover' | 'returns') => list.reduce((t, m) => t + (m.stat?.[k] || 0), 0)
    const count = sum('count')
    const turnover = sum('turnover')
    const returns = sum('returns')
    const avg = count ? Math.round(turnover / count) : 0

    const th = (k: SortKey | '', label: string, num?: boolean) => (
        <th
            className={num ? 'num' : undefined}
            onClick={
                k
                    ? () => {
                          if (sort === k) setDir(-dir)
                          else {
                              setSort(k)
                              setDir(k === 'name' ? 1 : -1)
                          }
                      }
                    : undefined
            }
            style={k ? { cursor: 'pointer', color: sort === k ? ui.pink : undefined } : undefined}
        >
            {label}
            {k && sort === k ? (dir < 0 ? ' ↓' : ' ↑') : ''}
        </th>
    )
    const toggle = (id: number) => setOpen((o) => ({ ...o, [id]: !o[id] }))

    return (
        <Box sx={pageSx}>
            <Box sx={contentSx}>
                <PageHead
                    title={formatMessage({ id: 'crmManagers' })}
                    sub={
                        <>
                            {formatMessage({ id: 'crmManagersSub' })}
                            {added && <Box sx={{ color: tone.ok.text, fontWeight: 600, marginTop: '6px' }}>{added}</Box>}
                        </>
                    }
                    action={
                        <PrimaryButton
                            onClick={() => {
                                setAdding(!adding)
                                setAdded('')
                            }}
                        >
                            {adding ? formatMessage({ id: 'crmClose' }) : `＋ ${formatMessage({ id: 'crmNewManager' })}`}
                        </PrimaryButton>
                    }
                />

                {adding && (
                    <AddManagerForm
                        managers={managers}
                        onAdded={(text) => {
                            setAdding(false)
                            setFired(false)
                            setAdded(text)
                        }}
                    />
                )}

                <Card sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <Chips>
                        <Chip on={!fired} onClick={() => setFired(false)}>
                            {formatMessage({ id: 'crmActiveManagers' })} ({managers.length - firedN})
                        </Chip>
                        <Chip on={fired} onClick={() => setFired(true)}>
                            {formatMessage({ id: 'crmFiredManagers' })} ({firedN})
                        </Chip>
                    </Chips>
                    <PeriodBar period={period} />
                    <Chips>
                        {['', ...SALES_GROUPS].map((g) => (
                            <Chip key={g || 'all'} on={group === g} onClick={() => setGroup(g)}>
                                {g ? dictLabel(formatMessage, 'Group', g) : formatMessage({ id: 'crmPreset_all' })} ({live.filter((m) => !g || m.salesGroup === g).length})
                            </Chip>
                        ))}
                    </Chips>
                    <Kpis
                        items={[
                            { label: formatMessage({ id: 'crmStudentShort' }), value: count },
                            {
                                label: group ? `${formatMessage({ id: 'crmKpiTurnover' })} · ${dictLabel(formatMessage, 'Group', group)}` : formatMessage({ id: 'crmKpiTurnover' }),
                                value: money(turnover),
                            },
                            { label: formatMessage({ id: 'crmKpiAvg' }), value: money(avg) },
                            {
                                label: formatMessage({ id: 'crmKpiReturns' }),
                                value: returns,
                                // сумма из общего отчёта - только когда и число из него же совпадает с суммой по списку
                                sub: !group && !fired && returns > 0 && report?.returns === returns && report?.returnsSum ? money(report.returnsSum) : undefined,
                                bad: returns > 0,
                            },
                        ]}
                    />
                </Card>
                {isFetching && !managers.length && <LinearProgress sx={{ marginBottom: 1 }} />}

                <Card pad={false}>
                    <TableWrap minWidth={820}>
                        <table>
                            <thead>
                                <tr>
                                    {th('', '№')}
                                    {th('name', formatMessage({ id: 'crmManager' }))}
                                    {th('count', formatMessage({ id: 'crmStudentShort' }), true)}
                                    {th('turnover', formatMessage({ id: 'crmKpiTurnover' }), true)}
                                    {th('avgCheck', formatMessage({ id: 'crmKpiAvg' }), true)}
                                    {th('returns', formatMessage({ id: 'crmKpiReturns' }), true)}
                                    {th('', '')}
                                </tr>
                            </thead>
                            <tbody>
                                {list.map((m, i) => {
                                    const s = m.stat
                                    const zero = !s?.turnover || !!m.firedAt
                                    return (
                                        <React.Fragment key={m.userId}>
                                            <tr
                                                tabIndex={0}
                                                onClick={() => toggle(m.userId)}
                                                onKeyDown={(e) => {
                                                    if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                                                        e.preventDefault()
                                                        toggle(m.userId)
                                                    }
                                                }}
                                                style={{ cursor: 'pointer', color: zero ? ui.muted : undefined }}
                                            >
                                                <td>{s?.turnover ? i + 1 : '-'}</td>
                                                <td>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                        <Box sx={{ ...avatar, width: 30, height: 30, fontSize: '11px' }}>{initials(m.name)}</Box>
                                                        <b style={{ color: ui.ink }}>
                                                            {open[m.userId] ? '▾' : '▸'} {m.name || '-'}
                                                        </b>
                                                        {m.salesGroup && (
                                                            <Pill tone={m.salesGroup === 'OP_RU' ? 'wait' : 'pink'} sx={{ padding: '1px 8px' }}>
                                                                {dictLabel(formatMessage, 'Group', m.salesGroup)}
                                                            </Pill>
                                                        )}
                                                        {m.firedAt && <Pill tone="bad">{formatMessage({ id: 'crmFiredOn' }, { date: fmtDate(m.firedAt) })}</Pill>}
                                                    </Box>
                                                </td>
                                                <td className="num">
                                                    <b>{s?.count ?? 0}</b>
                                                </td>
                                                <td className="num">
                                                    <b>{money(s?.turnover)}</b>
                                                </td>
                                                <td className="num">{money(s?.avgCheck)}</td>
                                                <td className="num" style={{ color: s?.returns ? tone.bad.text : undefined }}>{s?.returns ?? 0}</td>
                                                <td className="num" onClick={(e) => e.stopPropagation()}>
                                                    {!m.firedAt && (
                                                        <Box component="button" type="button" onClick={() => setCred(m)} sx={linkBtn}>
                                                            🔑 {formatMessage({ id: 'crmLoginBtn' })}
                                                        </Box>
                                                    )}
                                                    <Box
                                                        component="button"
                                                        type="button"
                                                        onClick={() => setManage(m)}
                                                        aria-label={`${m.name || ''} - ${formatMessage({ id: 'crmManage' })}`}
                                                        sx={{ ...linkBtn, fontSize: '16px', padding: '0 6px' }}
                                                    >
                                                        ⋯
                                                    </Box>
                                                </td>
                                            </tr>
                                            {open[m.userId] && (
                                                <tr className="det">
                                                    <td colSpan={7}>
                                                        <ManagerIntakes sellerId={m.userId} from={period.from} to={period.to} />
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    )
                                })}
                                {!list.length && <EmptyRow cols={7} text={formatMessage({ id: fired ? 'crmNoFired' : 'crmNoManagers' })} />}
                            </tbody>
                            {list.length > 0 && (
                                <tfoot>
                                    <tr>
                                        <td />
                                        <td>
                                            <b>{formatMessage({ id: 'crmTotalManagers' }, { n: list.length })}</b>
                                        </td>
                                        <td className="num">
                                            <b>{count}</b>
                                        </td>
                                        <td className="num">
                                            <b>{money(turnover)}</b>
                                        </td>
                                        <td className="num">
                                            <b>{money(avg)}</b>
                                        </td>
                                        <td className="num">{returns}</td>
                                        <td />
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </TableWrap>
                </Card>
                {cred && <CredDialog m={cred} onClose={() => setCred(null)} />}
                {manage && (
                    <ManageDialog
                        m={manage}
                        onClose={() => setManage(null)}
                        onDone={(text, toFired) => {
                            setManage(null)
                            setFired(toFired)
                            setAdded(text)
                        }}
                    />
                )}
            </Box>
        </Box>
    )
}

const linkBtn = {
    border: 'none',
    background: 'transparent',
    color: ui.pink,
    fontWeight: 600,
    fontSize: '12.5px',
    cursor: 'pointer',
    padding: '4px 2px',
    font: 'inherit',
    whiteSpace: 'nowrap',
}

// «🔑 Кіру»: логин и временный пароль; каждое открытие пишется в журнал
const CredDialog: React.FC<{ m: CrmManager; onClose: () => void }> = ({ m, onClose }) => {
    const { formatMessage } = useIntl()
    const { data, isFetching } = crmApi.useGetManagerCredentialsQuery(m.userId, { refetchOnMountOrArgChange: true })
    const [reset, { isLoading: resetting }] = crmApi.useResetManagerPasswordMutation()
    const [fresh, setFresh] = useState<CrmCredentials | null>(null)
    const [show, setShow] = useState(false)
    const [msg, setMsg] = useState('')
    const login = fresh?.login || data?.login || formatPhone(m.phone)
    const password = fresh?.tempPassword ?? data?.tempPassword ?? null

    const copy = async (text: string, ok: string) => {
        try {
            await navigator.clipboard.writeText(text)
            setMsg(formatMessage({ id: ok }))
        } catch {
            setMsg(formatMessage({ id: 'crmCopyFailed' }))
        }
    }
    const regen = async () => {
        try {
            const res = await reset(m.userId).unwrap()
            setFresh(res)
            setShow(true)
            setMsg(formatMessage({ id: 'crmPasswordRegenerated' }))
        } catch {
            /* общий обработчик */
        }
    }
    const message = `Dostyq Online\n${formatMessage({ id: 'crmSite' })}: ${window.location.host}\n${formatMessage({ id: 'crmLogin' })}: ${login}\n${formatMessage({ id: 'crmPasswordWord' })}: ${password || ''}`

    return (
        <CrmDialog title={formatMessage({ id: 'crmCredTitle' })} sub={`${m.name || '-'}${m.salesGroup ? ` · ${dictLabel(formatMessage, 'Group', m.salesGroup)}` : ''}`} onClose={onClose}>
            {isFetching && !data && <LinearProgress sx={{ marginTop: '12px' }} />}
            <FieldLabel>
                <Box sx={{ marginTop: '14px' }}>{formatMessage({ id: 'crmLoginPhoneShort' })}</Box>
            </FieldLabel>
            <Box sx={credRow}>
                <Box component="span" sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{login}</Box>
                <Box component="button" type="button" sx={linkBtn} onClick={() => copy(login, 'crmLoginCopied')}>
                    {formatMessage({ id: 'crmCopy' })}
                </Box>
            </Box>
            <FieldLabel>{formatMessage({ id: 'crmPasswordWord' })}</FieldLabel>
            <Box sx={credRow}>
                <Box component="span" sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere', fontFamily: 'monospace' }}>
                    {password ? (show ? password : '••••••••') : <Muted>{formatMessage({ id: 'crmPasswordChanged' })}</Muted>}
                </Box>
                {password && (
                    <>
                        <Box component="button" type="button" sx={linkBtn} onClick={() => setShow(!show)}>
                            {formatMessage({ id: show ? 'crmHideWord' : 'crmShow' })}
                        </Box>
                        <Box component="button" type="button" sx={linkBtn} onClick={() => copy(password, 'crmPasswordCopied')}>
                            {formatMessage({ id: 'crmCopy' })}
                        </Box>
                    </>
                )}
            </Box>
            <Box sx={{ color: tone.ok.text, fontSize: '13px', minHeight: '18px', fontWeight: 600 }}>{msg}</Box>
            <Hint sx={{ margin: '6px 0 14px' }}>{formatMessage({ id: 'crmCredHint' })}</Hint>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <OutlineButton disabled={resetting} onClick={regen}>
                    {formatMessage({ id: 'crmNewPassword' })}
                </OutlineButton>
                <PrimaryButton disabled={!password} onClick={() => copy(message, 'crmMessageCopied')}>
                    {formatMessage({ id: 'crmCopyForManager' })}
                </PrimaryButton>
            </Box>
        </CrmDialog>
    )
}

const credRow = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    border: `1px solid ${ui.border}`,
    borderRadius: '10px',
    padding: '8px 12px',
    marginBottom: '12px',
    fontSize: '14px',
    fontWeight: 700,
}

// «⋯»: уволить / вернуть / удалить (удалить - только если менеджер не зарегистрировал ни одного ученика)
const ManageDialog: React.FC<{ m: CrmManager; onClose: () => void; onDone: (text: string, fired: boolean) => void }> = ({ m, onClose, onDone }) => {
    const { formatMessage } = useIntl()
    const [date, setDate] = useState(todayKey())
    const [reason, setReason] = useState(FIRE_REASONS[0])
    const [ask, setAsk] = useState(false)
    const [fire, { isLoading: firing }] = crmApi.useFireManagerMutation()
    const [rehire, { isLoading: rehiring }] = crmApi.useRehireManagerMutation()
    const [remove, { isLoading: removing }] = crmApi.useDeleteManagerMutation()
    const name = m.name || ''
    const total = m.intakesTotal || 0

    const act = async (fn: () => Promise<unknown>, text: string, toFired: boolean) => {
        try {
            await fn()
            onDone(text, toFired)
        } catch {
            /* общий обработчик */
        }
    }

    return (
        <CrmDialog
            title={name || '-'}
            sub={
                <>
                    {m.salesGroup ? dictLabel(formatMessage, 'Group', m.salesGroup) : ''}
                    {m.firedAt && (
                        <Box component="span" sx={{ color: TONES.bad.text }}>
                            {' · '}
                            {formatMessage({ id: 'crmFiredOn' }, { date: fmtDate(m.firedAt) })}
                            {m.fireReason ? ` - ${dictLabel(formatMessage, 'Fire', m.fireReason)}` : ''}
                        </Box>
                    )}
                </>
            }
            onClose={onClose}
        >
            {m.firedAt ? (
                <Box sx={mbox}>
                    <Box sx={mboxTitle}>{formatMessage({ id: 'crmRehire' })}</Box>
                    <Hint sx={{ marginBottom: '10px' }}>{formatMessage({ id: 'crmRehireHint' })}</Hint>
                    <PrimaryButton disabled={rehiring} onClick={() => act(() => rehire(m.userId).unwrap(), formatMessage({ id: 'crmRehiredToast' }, { name }), false)}>
                        {formatMessage({ id: 'crmRehire' })}
                    </PrimaryButton>
                </Box>
            ) : (
                <Box sx={mbox}>
                    <Box sx={mboxTitle}>{formatMessage({ id: 'crmFire' })}</Box>
                    <Box sx={{ marginBottom: '8px' }}>
                        <FieldLabel>{formatMessage({ id: 'crmFiredAt' })}</FieldLabel>
                        <TextField type="date" size="small" fullWidth value={date} onChange={(e) => setDate(e.target.value)} />
                    </Box>
                    <Box sx={{ marginBottom: '8px' }}>
                        <FieldLabel>{formatMessage({ id: 'crmReason' })}</FieldLabel>
                        <Select size="small" fullWidth value={reason} onChange={(e) => setReason(e.target.value)}>
                            {FIRE_REASONS.map((r) => (
                                <MenuItem key={r} value={r}>{dictLabel(formatMessage, 'Fire', r)}</MenuItem>
                            ))}
                        </Select>
                    </Box>
                    <Hint sx={{ marginBottom: '10px' }}>{formatMessage({ id: 'crmFireHint' })}</Hint>
                    <OutlineButton
                        color="error"
                        disabled={firing || !date}
                        onClick={() => act(() => fire({ id: m.userId, date, reason }).unwrap(), formatMessage({ id: 'crmFiredToast' }, { name }), true)}
                    >
                        {formatMessage({ id: 'crmFire' })}
                    </OutlineButton>
                </Box>
            )}
            <Box sx={mbox}>
                <Box sx={mboxTitle}>{formatMessage({ id: 'crmDeleteManager' })}</Box>
                {total > 0 ? (
                    <Hint>{formatMessage({ id: 'crmCantDeleteManager' }, { n: total })}</Hint>
                ) : ask ? (
                    <>
                        <Box sx={{ color: TONES.bad.text, fontSize: '13px', marginBottom: '10px' }}>{formatMessage({ id: 'crmDeleteManagerAsk' })}</Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <PrimaryButton
                                color="error"
                                disabled={removing}
                                onClick={() => act(() => remove(m.userId).unwrap(), formatMessage({ id: 'crmDeleteManagerDone' }, { name }), !!m.firedAt)}
                            >
                                {formatMessage({ id: 'crmYesDelete' })}
                            </PrimaryButton>
                            <OutlineButton onClick={() => setAsk(false)}>{formatMessage({ id: 'crmNo' })}</OutlineButton>
                        </Box>
                    </>
                ) : (
                    <>
                        <Hint sx={{ marginBottom: '10px' }}>{formatMessage({ id: 'crmCanDeleteManager' })}</Hint>
                        <OutlineButton color="error" onClick={() => setAsk(true)}>
                            {formatMessage({ id: 'crmDeleteManager' })}
                        </OutlineButton>
                    </>
                )}
            </Box>
        </CrmDialog>
    )
}

const mbox = { border: `1px solid ${ui.border}`, borderRadius: '12px', padding: '12px 14px', marginTop: '14px' }
const mboxTitle = { fontWeight: 700, fontSize: '14px', marginBottom: '8px' }

const ManagerIntakes: React.FC<{ sellerId: number; from: string; to: string }> = ({ sellerId, from, to }) => {
    const { formatMessage } = useIntl()
    const { data = [], isFetching } = crmApi.useListIntakesQuery({ from, to, sellerId })
    const dict = useCrmDict()
    // платежи рассрочки менеджера - отдельные строки «N-ай төлемі», как в «Менің оқушыларым»
    const { data: inst = [] } = crmApi.useListInstallmentsQuery('ROP')
    const lines = useMemo(() => {
        const pays = inst
            .flatMap((x) => (x.payments ?? []).map((p) => ({ p, x })))
            .filter(({ p, x }) => (p.sellerId ?? x.sellerId) === sellerId && p.monthNo > 1 && dayKeyOf(p.createdAt) >= from && dayKeyOf(p.createdAt) <= to)
        return [...data.map((r) => ({ at: r.createdAt, r, pay: null as null | (typeof pays)[number] })), ...pays.map((pay) => ({ at: pay.p.createdAt, r: pay.x, pay }))].sort((a, b) =>
            a.at < b.at ? 1 : -1,
        )
    }, [data, inst, sellerId, from, to])
    const rows = lines
    if (isFetching && !data.length) return <LinearProgress />
    return (
        <TableWrap minWidth={480}>
            <table>
                <thead>
                    <tr>
                        <th>{formatMessage({ id: 'crmColDate' })}</th>
                        <th>{formatMessage({ id: 'crmColStudent' })}</th>
                        <th>{formatMessage({ id: 'crmColPackage' })}</th>
                        <th className="num">{formatMessage({ id: 'crmColSum' })}</th>
                        <th>{formatMessage({ id: 'crmColStage' })}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(({ r, pay }) => {
                        if (pay)
                            return (
                                <tr key={`p${pay.p.id}`}>
                                    <td>{fmtDate(pay.p.createdAt)}</td>
                                    <td>
                                        {r.data?.studentName}
                                        <Muted>{formatMessage({ id: 'crmMonthlyPayment' })}</Muted>
                                    </td>
                                    <td>{dict.label('PACKAGE', r.data?.packageCode)}</td>
                                    <td className="num">{money(pay.p.amount)}</td>
                                    <td>
                                        <Pill tone={pay.p.status === 'CONFIRMED' ? 'ok' : 'blue'}>
                                            {formatMessage({ id: 'crmMonthN' }, { n: pay.p.monthNo })}
                                            {pay.p.status === 'CONFIRMED' ? ' ✓' : ''}
                                        </Pill>
                                    </td>
                                </tr>
                            )
                        const st = sellerStage(formatMessage, r)
                        return (
                            <tr key={r.id} className={r.status === 'RETURNED' ? 'ret' : ''}>
                                <td>{fmtDate(r.createdAt)}</td>
                                <td>{r.data?.studentName}</td>
                                <td>{dict.label('PACKAGE', r.data?.packageCode)}</td>
                                <td className="num">{money(r.data?.paidSum)}</td>
                                <td>
                                    <Pill tone={st.tone}>{st.text}</Pill>
                                </td>
                            </tr>
                        )
                    })}
                    {!rows.length && <EmptyRow cols={5} text={formatMessage({ id: 'crmPeriodNone' })} />}
                </tbody>
            </table>
        </TableWrap>
    )
}

const AddManagerForm: React.FC<{ managers: CrmManager[]; onAdded: (text: string) => void }> = ({ managers, onAdded }) => {
    const { formatMessage } = useIntl()
    const [add, { isLoading }] = crmApi.useAddManagerMutation()
    const [name, setName] = useState('')
    const [phone, setPhone] = useState('')
    const [group, setGroup] = useState('')
    const [hiredAt, setHiredAt] = useState(todayKey())
    const [errors, setErrors] = useState<Record<string, string>>({})

    const submit = async (e: React.FormEvent) => {
        e.preventDefault()
        const err: Record<string, string> = {}
        const n = name.trim()
        if (!n) err.name = formatMessage({ id: 'crmRequired' })
        else if (managers.some((m) => (m.name || '').toLowerCase() === n.toLowerCase())) err.name = formatMessage({ id: 'crmManagerExists' })
        if (digits(phone).length !== 11) err.phone = formatMessage({ id: 'crmPhone11' })
        else if (managers.some((m) => digits(formatPhone(m.phone)) === digits(phone))) err.phone = formatMessage({ id: 'crmManagerPhoneExists' })
        if (!group) err.group = formatMessage({ id: 'crmPickGroupShort' })
        setErrors(err)
        if (Object.keys(err).length) return
        try {
            const res = await add({ name: n, phone: digits(phone), salesGroup: group, hiredAt }).unwrap()
            onAdded(
                formatMessage(
                    { id: 'crmManagerAdded' },
                    { name: n, group: dictLabel(formatMessage, 'Group', group), login: res?.login || formatPhone(phone), password: res?.tempPassword || '-' },
                ),
            )
        } catch {
            /* общий обработчик */
        }
    }

    return (
        <Card>
            <CardHead title={formatMessage({ id: 'crmAddManager' })} />
            <form onSubmit={submit} noValidate>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: '12px' }}>
                    <Box>
                        <FieldLabel required>{formatMessage({ id: 'crmFullName' })}</FieldLabel>
                        <TextField
                            size="small"
                            fullWidth
                            autoComplete="off"
                            value={name}
                            error={!!errors.name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={formatMessage({ id: 'crmManagerNamePlaceholder' })}
                        />
                        <ErrorText text={errors.name} />
                    </Box>
                    <Box>
                        <FieldLabel required>{formatMessage({ id: 'crmPhone' })}</FieldLabel>
                        <TextField
                            size="small"
                            fullWidth
                            value={phone}
                            error={!!errors.phone}
                            inputProps={{ inputMode: 'tel' }}
                            placeholder="+7 7__ ___ __ __"
                            onChange={(e) => setPhone(e.target.value)}
                            onBlur={() => setPhone(formatPhone(phone))}
                        />
                        <ErrorText text={errors.phone} />
                    </Box>
                    <Box>
                        <FieldLabel required>{formatMessage({ id: 'crmSalesGroup' })}</FieldLabel>
                        <Select size="small" fullWidth displayEmpty value={group} error={!!errors.group} onChange={(e) => setGroup(e.target.value)}>
                            <MenuItem value="" disabled>{formatMessage({ id: 'crmChoose' })}</MenuItem>
                            {SALES_GROUPS.map((g) => (
                                <MenuItem key={g} value={g}>{dictLabel(formatMessage, 'Group', g)}</MenuItem>
                            ))}
                        </Select>
                        <ErrorText text={errors.group} />
                    </Box>
                    <Box>
                        <FieldLabel>{formatMessage({ id: 'crmHiredAt' })}</FieldLabel>
                        <TextField type="date" size="small" fullWidth value={hiredAt} onChange={(e) => setHiredAt(e.target.value)} />
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', marginTop: '12px' }}>
                    <PrimaryButton type="submit" disabled={isLoading}>{formatMessage({ id: 'crmSave' })}</PrimaryButton>
                    <Hint>{formatMessage({ id: 'crmAddManagerHint' })}</Hint>
                </Box>
            </form>
        </Card>
    )
}

export default RopManagersPage
